
import React, { Suspense, useEffect, useState } from "react";
import {
  Box,
  Card,
  InlineStack,
  Button,
  Text,
  Pagination,
  SkeletonBodyText,
} from '@shopify/polaris';
import ProductCard from "./ProductCard.react.js";
import { PRODUCT_IMAGE_PLACEHOLDER } from "../CommonComponents/DeferredMediaImage.react.js";
import { gql, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { i18 } from "./ProductSearchUtil.js";

const RatingModal = React.lazy(() => import("../CommonComponents/RatingModal.react.js"));
const MIN_SEARCH_PAGE_SIZE = 12;
const MAX_SEARCH_PAGE_SIZE = 24;

const getSearchPageSize = (rowsNumber) => {
  const visibleColumns = Number(rowsNumber);
  if (!Number.isFinite(visibleColumns) || visibleColumns <= 0) return MIN_SEARCH_PAGE_SIZE;
  return Math.min(MAX_SEARCH_PAGE_SIZE, Math.max(MIN_SEARCH_PAGE_SIZE, visibleColumns * 4));
};

const NEW_GET_PRODUCTS = gql`
  query newSearchProducts(
    $titleSearch: String
    $category: String
    $sortBy: String
    $minOrders: Int
    $maxOrders: Int
    $maxPrice: Int
    $minFeedbackRate: Float
    $minPrice: Int
    $maxFeedbackRate: Float
    $minItemAsDescribed: Float
    $maxItemAsDescribed: Float
    $minShippingSpeed: Float
    $maxShippingSpeed: Float
    $minCommunication: Float
    $maxCommunication: Float
    $shipFromUs: Boolean
    $shipDay: Float
    $isFreeShip: Boolean
    $hasVideo: Boolean
    $IP: String
    $sessionID: String
    $pageSize: Int
    $pageIndex: Int
    $minReviewScore: Float
    $maxReviewScore: Float
  ) {
    newSearchProducts(
      titleSearch: $titleSearch
      category: $category
      sortBy: $sortBy
      minOrders: $minOrders
      maxOrders: $maxOrders
      maxPrice: $maxPrice
      minFeedbackRate: $minFeedbackRate
      minPrice: $minPrice
      maxFeedbackRate: $maxFeedbackRate
      minItemAsDescribed: $minItemAsDescribed
      maxItemAsDescribed: $maxItemAsDescribed
      minShippingSpeed: $minShippingSpeed
      maxShippingSpeed: $maxShippingSpeed
      minCommunication: $minCommunication
      maxCommunication: $maxCommunication
      shipFromUS: $shipFromUs
      shipDay: $shipDay
      isFreeShip: $isFreeShip
      hasVideo: $hasVideo
      IP: $IP
      sessionID: $sessionID
      pageSize: $pageSize
      pageIndex: $pageIndex
      minReviewScore: $minReviewScore
      maxReviewScore: $maxReviewScore
    ) {
      status
      result {
        id
        title
        numberSold
        reviewScore
        thumbnailSource
        price
        productLink
        feedbackScore
        itemAsDescribed
        shipSpeed
        communication
        images
        videoLink
        minSalePrice
        maxSalePrice
        minOriginalPrice
        maxOriginalPrice
        currency
        productRating
        discount
        isFreeShip
      }
      maximum
      savedList
      message
    }
  }
`;

export default function CardList(props) {
  const {
    cardWidth,
    rowsNumber,
    currentCategoryValue,
    currentSort,
    minPriceRange,
    maxPriceRange,
    currentPriceRange,
    currentTitle,
    currencySymbol,
    setIsShowImportURLComponent,
    isShowImportURLComponent,
    hasChromeExtension,
    onClickProductDetail,
    currency,
  } = props;
  const navigate = useNavigate();

  // 🚀 FIX: Declare state variables BEFORE they're used in useEffect
  const [pageIndex, setPageIndex] = useState(1);
  const [initialize, setInitialize] = useState(true);
  const pageSize = getSearchPageSize(rowsNumber);

  // 🚀 FIX: Remove skip option - let the query run automatically with proper variables
  // The query will run automatically when the component mounts
  const { loading, data, refetch, error, networkStatus } = useQuery(NEW_GET_PRODUCTS, {
    fetchPolicy: "cache-and-network", // Always fetch fresh data
    notifyOnNetworkStatusChange: true,
    // Return cached data immediately while fetching in background
    returnPartialData: true,
    // 🚀 FIX: Don't skip - let query run automatically, but only after initialize is false
    skip: initialize, // Only skip if we're still initializing
    // Add default variables to ensure query structure is valid
    variables: {
      titleSearch: currentTitle || "",
      category: currentCategoryValue || "all",
      sortBy: "default",
      pageIndex: 1,
      pageSize,
    },
  });

  // 🚀 Check if data is being fetched (initial load or background refetch)
  // networkStatus === 4 means refetch (background fetch with cached data)
  // networkStatus === 1 means initial loading
  const isLoadingData = loading || networkStatus === 1 || networkStatus === 4;
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const searchProduct = () => {
    let minPrice = "";
    let maxPrice = "";
    let sortBy = "default";
    if (currentPriceRange.includes("Under")) {
      minPrice = currentPriceRange.replace("Under ", "");
    } else if (currentPriceRange == "custom") {
      minPrice = minPriceRange;
      maxPrice = maxPriceRange;
    } else if (currentPriceRange != "clear" && currentPriceRange != "Price Range") {

      const priceList = currentPriceRange.split(" ~ ");
      if (priceList.length > 1) {
        minPrice = priceList[0];
        maxPrice = priceList[1];
      }
    }
    switch (currentSort) {
      case "Default":
        sortBy = "default";
        break;
      case "Price: Low to High":
        sortBy = "priceAsc";
        break;
      case "Price: High to Low":
        sortBy = "priceDesc";
        break;
      case "Sales: High to Low":
        sortBy = "salesDesc";
        break;
      default:
        sortBy = "default";
    }
    let variables = {
      category: currentCategoryValue,
      sortBy,
      minPrice: castStringtoFloat(minPrice != null ? minPrice.replace(currencySymbol, "") : ""),
      maxPrice: castStringtoFloat(maxPrice != null ? maxPrice.replace(currencySymbol, "") : ""),
      titleSearch: currentTitle,
      pageIndex,
      pageSize,
    };
    document.documentElement.scrollTop = 0;
    refetch(variables);
  }

  // 🚀 FIX: Set initialize to false on mount so the query can run automatically
  useEffect(() => {
    if (initialize) {
      setInitialize(false);
      // Query will run automatically once initialize is false (skip: initialize)
    }
  }, [initialize]);

  useEffect(() => {
    if (!initialize) {
      searchProduct();
    }
  }, [pageIndex]);
  useEffect(() => {
    if (!initialize) {
      if (pageIndex !== 1) {
        setPageIndex(1);
      } else {
        searchProduct();
      }
    }
  }, [currentTitle, minPriceRange, maxPriceRange, currentCategoryValue, currentSort, currency]);
  useEffect(() => {
    if (!initialize && ((currentPriceRange == "custom" && (minPriceRange != null || maxPriceRange != null)) || currentPriceRange !== "custom")) {
      if (pageIndex !== 1) {
        setPageIndex(1);
      } else {
        searchProduct();
      }
    }
  }, [currentPriceRange]);

  // Do not preload supplier product images. Remote AliExpress/Shopify files
  // become a late LCP candidate if they start downloading during first paint.

  // 🚀 FIX: Show loading state when query is loading OR when data is not yet available
  // When skip: true, loading is false, so we need to check both loading and data
  const isLoading = loading || initialize || (data == null && !error);
  const newSearchProducts = data?.newSearchProducts;
  const productResults = Array.isArray(newSearchProducts?.result) ? newSearchProducts.result : null;
  const hasData = newSearchProducts?.status !== false && productResults != null;

  if (!hasData && !isLoading) {
    return <Box style={{ display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap" }} >{i18("Something wrong, please refresh the window.")}</Box>
  }
  const showSkeleton = !hasData && isLoading;
  const slotCount = getSearchPageSize(rowsNumber);
  const columnCount = Math.max(1, Number(rowsNumber) || 1);
  const rowCount = Math.ceil(slotCount / columnCount);
  const gridMinHeight = rowCount * 400 + Math.max(0, rowCount - 1) * 10;
  let maxPageCount = 100;
  if (newSearchProducts?.maximum != null) {
    maxPageCount = newSearchProducts.maximum < 100 ? newSearchProducts.maximum : 100;
  }
  return (
    <>
      {isRatingModalOpen ? (
        <Suspense fallback={null}>
          <RatingModal
            isRatingModalOpen={isRatingModalOpen}
            setIsRatingModalOpen={setIsRatingModalOpen}
            type="import"
          />
        </Suspense>
      ) : null}
      <Box style={{ display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap", minHeight: `${gridMinHeight}px` }} >
        {Array.from({ length: slotCount }, (_, index) => {
          const product = showSkeleton ? null : productResults[index];
          return <div key={`search-slot-${index}`} style={{ width: cardWidth, height: "400px", flex: "0 0 auto", overflow: "hidden" }}>
            {product == null ? <Box style={{ width: "100%", height: "400px", boxSizing: "border-box", overflow: "hidden", background: showSkeleton ? "#FFF" : "transparent", borderRadius: "5px", boxShadow: showSkeleton ? "0px 2px 1px -1px #00000033, 0px 1px 1px 0px #00000024, 0px 1px 3px 0px #0000001F" : "none", padding: showSkeleton && index !== 0 ? "16px" : "0px" }}>
              {showSkeleton ? <Box style={{ height: "100%" }}>
                {index === 0 ? <img src={PRODUCT_IMAGE_PLACEHOLDER} alt="" width="280" height="280" decoding="async" fetchPriority="high" style={{ width: "100%", height: "280px", objectFit: "cover", display: "block" }} /> : <Box style={{ width: "100%", height: "200px", background: "rgba(227, 227, 227, 1)", borderRadius: "0.5rem" }} />}
                <Box style={{ marginTop: "20px", paddingInline: index === 0 ? "16px" : "0px" }}>
                  <SkeletonBodyText lines={2} />
                </Box>
                <Box style={{ marginTop: "20px", width: "80px", height: "8px", background: "rgba(227, 227, 227, 1)", borderRadius: "0.5rem" }} />
                <Box style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
                  <Box style={{ width: "120px", height: "28px", background: "rgba(227, 227, 227, 1)", borderRadius: "0.5rem" }} />
                  <Box style={{ width: "80px", height: "28px", background: "rgba(227, 227, 227, 1)", borderRadius: "0.5rem" }} />
                  <Box style={{ width: "80px", height: "28px", background: "rgba(227, 227, 227, 1)", borderRadius: "0.5rem" }} />
                </Box>
              </Box> : null}
            </Box> : <ProductCard
              cardWidth={cardWidth}
              index={1}
              isLcpCandidate={index === 0}
              title={product.title}
              numberSold={product.numberSold}
              id={product.id}
              isSaved={newSearchProducts?.savedList != null ? newSearchProducts.savedList.includes(product.id) : false}
              images={product.images}
              videoLink={product.videoLink}
              minSalePrice={product.minSalePrice}
              currency={product.currency}
              productLink={product.productLink}
              productRating={product.productRating}
              discount={product.discount}
              hasChromeExtension={hasChromeExtension}
              onClickProductDetail={onClickProductDetail}
              isFreeShip={product.isFreeShip}
              setIsRatingModalOpen={setIsRatingModalOpen}
              isPriority={index < rowsNumber}
            />}
          </div>
        })}
      </Box>

      <Box style={{ padding: "20px 0px" }}>
        <InlineStack gap="400" align="center" blockAlign="center">
          <Text variant="bodyMd">{i18("Don’t you see the product you are looking for?")}</Text>
          <Button variant="primary" size="medium" style={{ background: "#FF9711" }} onClick={() => {
            // navigate("/importURL");
            setIsShowImportURLComponent(true);
          }}>{i18("Import by URL")}</Button>
        </InlineStack>
      </Box>

      <Card padding="0" roundedAbove="0">
        <Box style={{ padding: "16px 0px" }}>
          <InlineStack gap="400" align="center" blockAlign="center">
            <Pagination
              style={{ background: "red" }}
              onPrevious={() => {
                setPageIndex(pageIndex - 1);
              }}
              onNext={() => {
                setPageIndex(pageIndex + 1);
              }}
              type="page"
              hasNext={pageIndex < maxPageCount}
              hasPrevious={pageIndex > 1}
              label={<PaginationButtonGroup
                currentPageIndex={pageIndex}
                setPageIndex={setPageIndex}
                maximum={maxPageCount}
              />}
            />
          </InlineStack>
        </Box>
      </Card>
    </>
  )
}

function PaginationButton(props) {
  const { pageIndex = 1, active = false, onClick } = props;
  return <Box style={{
    padding: " 4px 8px ",
    background: active ? "#E3E3E3" : "#0000000A",
    cursor: "pointer"
  }} onClick={onClick}>
    {/* <Button size="micro"> */}
    <Box style={{ color: active ? "#4A4A4A" : "#CCCCCC", minWidth: "12px" }}>
      <Text variant="headingSm" alignment="center">{pageIndex}</Text>
    </Box>
    {/* </Button> */}
  </Box>
}

function PaginationButtonGroup(props) {
  const { currentPageIndex = 1, setPageIndex, maximum = 5 } = props;
  const [isShowFirstApostrophe, setIsShowFirstApostrophe] = useState(false);
  const [isShowSecondApostrophe, setIsShowSecondApostrophe] = useState(true);
  const [firstButtonIndex, setFirstButtonIndex] = useState(currentPageIndex);
  const [secondButtonIndex, setSecondButtonIndex] = useState(currentPageIndex + 1);
  const [thirdButtonIndex, setThirdButtonIndex] = useState(currentPageIndex + 2);
  const [fourthButtonIndex, setFourthButtonIndex] = useState(currentPageIndex + 3);

  useEffect(() => {
    if (currentPageIndex <= maximum - 4) {
      setFirstButtonIndex(currentPageIndex);
      setSecondButtonIndex(currentPageIndex + 1);
      setThirdButtonIndex(currentPageIndex + 2);
      setFourthButtonIndex(currentPageIndex + 3);
      setIsShowSecondApostrophe(currentPageIndex + 3 < maximum - 1);
      setIsShowFirstApostrophe(currentPageIndex > 2);
    } else {
      const difference = 4 - (maximum - currentPageIndex);
      setFirstButtonIndex(currentPageIndex - difference);
      setSecondButtonIndex(currentPageIndex + 1 - difference);
      setThirdButtonIndex(currentPageIndex + 2 - difference);
      setFourthButtonIndex(currentPageIndex + 3 - difference);
      setIsShowSecondApostrophe(currentPageIndex + 3 - difference < maximum - 1);
      setIsShowFirstApostrophe(currentPageIndex - difference > 2);
    }
  }, [currentPageIndex]);

  if (maximum === 0) {
    const arr = new Array(maximum).fill(0);
    return <div style={{ color: "#ccc" }}>
      <InlineStack align="center" blockAlign="center" gap="400">
        <PaginationButton active={currentPageIndex == 1} pageIndex={1} onClick={() => {
          setPageIndex(1);
        }} />
      </InlineStack>
    </div>
  } else if (maximum <= 6) {
    const arr = new Array(maximum).fill(0);
    return <div style={{ color: "#ccc" }}>
      <InlineStack align="center" blockAlign="center" gap="400">
        {
          arr.map((_item, index) => {
            return <PaginationButton active={currentPageIndex == (index + 1)} pageIndex={index + 1} onClick={() => {
              setPageIndex(index + 1);
            }} key={index} />
          })
        }
      </InlineStack>
    </div>
  }
  return <div style={{ color: "#ccc" }}>
    <InlineStack align="center" blockAlign="center" gap="400">
      {firstButtonIndex !== 1 ? <PaginationButton active={currentPageIndex == 1} pageIndex={1} onClick={() => {
        setPageIndex(1)
      }} /> : null}
      {isShowFirstApostrophe ? <Text variant="headingSm">...</Text> : null}
      <PaginationButton active={currentPageIndex == firstButtonIndex} pageIndex={firstButtonIndex} onClick={() => {
        setPageIndex(firstButtonIndex)
      }} />
      <PaginationButton active={currentPageIndex == secondButtonIndex} pageIndex={secondButtonIndex} onClick={() => {
        setPageIndex(secondButtonIndex)
      }} />
      <PaginationButton active={currentPageIndex == thirdButtonIndex} pageIndex={thirdButtonIndex} onClick={() => {
        setPageIndex(thirdButtonIndex)
      }} />
      <PaginationButton active={currentPageIndex == fourthButtonIndex} pageIndex={fourthButtonIndex} onClick={() => {
        setPageIndex(fourthButtonIndex)
      }} />
      {isShowSecondApostrophe ? <Text variant="headingSm">...</Text> : null}
      {fourthButtonIndex !== maximum ? <PaginationButton active={currentPageIndex == maximum} pageIndex={maximum} onClick={() => {
        setPageIndex(maximum)
      }} /> : null}
    </InlineStack>
  </div>
}

function castStringtoFloat(currentString) {
  if (currentString === "" || currentString == null) {
    return null;
  }
  return parseFloat(currentString);
}
